/**
 * ConsentNotice — the legal acknowledgement shown when creating an avatar from
 * a real person's likeness (photo / digital twin). Collapsed by default to a
 * one-line summary with an expandable full text. Controlled checkbox.
 *
 * NOTE on HeyGen: per the Create Avatar docs, HeyGen's API consent FLOW (the
 * browser approval URL) is only required for `digital_twin`. Photo/prompt
 * avatars don't need it. This checkbox is Vivideo's own legal/ethics gate for
 * uploading someone's likeness — independent of HeyGen's flow.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ShieldCheck } from 'lucide-react';
import { cn } from '@/v2/utils';

export function ConsentNotice({
  checked,
  onChange,
  className,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  className?: string;
}) {
  const { t } = useTranslation('v2');
  const [open, setOpen] = useState(false);
  return (
    <div className={cn('rounded-xl border border-glass-border bg-surface-light/60 p-3', className)}>
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          className="mt-0.5 w-4 h-4 accent-primary shrink-0"
        />
        <span className="text-xs text-text-secondary leading-relaxed">
          <span className="inline-flex items-center gap-1 font-medium text-text-primary">
            <ShieldCheck className="w-3.5 h-3.5 text-primary" />
            {t('consentNotice.summary')}
          </span>
        </span>
      </label>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="mt-1.5 ml-7 inline-flex items-center gap-1 text-[11px] text-primary/90 hover:text-primary"
      >
        {open ? t('consentNotice.hide') : t('consentNotice.readFullTerms')}
        <ChevronDown className={cn('w-3 h-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <p className="mt-2 ml-7 text-[11px] leading-relaxed text-text-tertiary animate-fade-in">
          {t('consentNotice.fullText')}{' '}
          {t('consentNotice.acceptPrefix')}{' '}
          <a href="https://vivideo.ai/terms" target="_blank" rel="noopener noreferrer" className="text-primary/90 hover:underline">{t('consentNotice.termsOfService')}</a>
          {' '}{t('consentNotice.and')}{' '}
          <a href="https://vivideo.ai/privacy" target="_blank" rel="noopener noreferrer" className="text-primary/90 hover:underline">{t('consentNotice.privacyPolicy')}</a>.
        </p>
      )}
    </div>
  );
}
